import {
  type FC,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AppBar,
  Box,
  Button,
  Card,
  Divider,
  Grid,
  IconButton,
  Toolbar,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  useContentTimecodesQuery,
  useUpdateTimecodesMutation,
} from "@/partner/entities/contents";
import { Player } from "@/shared/components/Player";
import { useIsMobile } from "@/shared/hooks";
import { useVideoProviderTextKeys } from "../../model";
import { TimecodesList } from "./TimecodesList";
import { TimecodeFormDialog } from "./TimecodeFormDialog";
import type { EditableTimecode } from "./types";

interface TimecodesScreenProps {
  onClose: () => void;
  contentId: string;
  videoDuration?: number;
  hlsUrl?: string | null;
  thumbnailUrl?: string | null;
  /** Вкладки формы видео (VideoTabsMenu) — рендерятся под шапкой. */
  menu?: ReactNode;
}

// Порт референсного VideoForm/Timecodes/Timecodes.js: вкладка «Метки» формы
// видео — список слева, превью-плеер справа (маркеры на сикбаре и аннотации
// в нём отражают локальные правки сразу), сохранение одной мутацией
// updateTimecodes. Рендерится внутри EditVideoDialog по ?tab=timecodes.
export const TimecodesScreen: FC<TimecodesScreenProps> = ({
  onClose,
  contentId,
  videoDuration = 0,
  hlsUrl,
  thumbnailUrl,
  menu,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const providerTextKeys = useVideoProviderTextKeys();
  const isMobile = useIsMobile("(max-width: 600px)");

  const [dialogMode, setDialogMode] = useState<"create" | "edit" | null>(null);
  const [editTimecode, setEditTimecode] = useState<EditableTimecode | null>(
    null
  );
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [dialogCurrentTime, setDialogCurrentTime] = useState(0);
  const [localTimecodes, setLocalTimecodes] = useState<EditableTimecode[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Видеоэлемент превью-плеера: пауза при создании метки, перемотка по клику
  // в списке, текущее время для новой метки (референс делал это через
  // playerRef.videoActions/videoState).
  const videoElRef = useRef<HTMLVideoElement | null>(null);

  const { data, loading, refetch } = useContentTimecodesQuery({
    variables: { id: contentId },
    skip: !contentId,
  });

  const [updateTimecodes, { loading: saving }] = useUpdateTimecodesMutation({
    onCompleted: () => {
      enqueueSnackbar(t("videos.timecodes.saved"), { variant: "success" });
      setHasUnsavedChanges(false);
      void refetch();
    },
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });

  const serverTimecodes = useMemo<EditableTimecode[]>(() => {
    const timecodes = data?.contentTimecodes?.timecodes ?? [];
    return [...timecodes]
      .map((timecode) => ({
        id: String(timecode.id),
        type: timecode.type,
        time: timecode.time,
        title: timecode.title,
        description: timecode.description,
        icon: timecode.icon,
        duration: timecode.duration,
        position: timecode.position,
        linkedContent: timecode.linkedContent
          ? {
              id: String(timecode.linkedContent.id),
              title: timecode.linkedContent.title,
            }
          : null,
      }))
      .sort((a, b) => a.time - b.time);
  }, [data?.contentTimecodes?.timecodes]);

  // Синхронизация локального стейта с сервером
  useEffect(() => {
    setLocalTimecodes(serverTimecodes);
    setHasUnsavedChanges(false);
  }, [serverTimecodes]);

  // Предупреждение о несохранённых правках при закрытии вкладки
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const onClickCreateTimecode = () => {
    const video = videoElRef.current;
    video?.pause();
    setDialogCurrentTime(Math.round(video?.currentTime ?? 0));
    setDialogMode("create");
  };

  const onClickEdit = (index: number) => {
    setEditTimecode(localTimecodes[index] ?? null);
    setEditIndex(index);
    setDialogMode("edit");
  };

  const onCloseFormDialog = () => {
    setDialogMode(null);
    setEditTimecode(null);
    setEditIndex(null);
    setDialogCurrentTime(0);
  };

  const onTimecodeClick = (time: number) => {
    const video = videoElRef.current;
    if (video) video.currentTime = time;
  };

  const onFormCompleted = (timecodeData: EditableTimecode) => {
    if (dialogMode === "create") {
      setLocalTimecodes((prev) =>
        [...prev, timecodeData].sort((a, b) => a.time - b.time)
      );
    } else if (dialogMode === "edit" && editIndex !== null) {
      setLocalTimecodes((prev) =>
        prev
          .map((timecode, index) =>
            index === editIndex ? timecodeData : timecode
          )
          .sort((a, b) => a.time - b.time)
      );
    }
    setHasUnsavedChanges(true);
    onCloseFormDialog();
    // Продолжаем воспроизведение после создания/правки метки (референс)
    void videoElRef.current?.play();
  };

  const onDeleteTimecode = (index: number) => {
    setLocalTimecodes((prev) => prev.filter((_, i) => i !== index));
    setHasUnsavedChanges(true);
  };

  const handleSave = () => {
    // Очищаем от серверных полей — остаются только поля TimecodeInput
    const cleanTimecodes = localTimecodes.map((timecode) => ({
      type: timecode.type,
      time: timecode.time,
      title: timecode.title,
      description: timecode.description || null,
      icon: timecode.icon,
      duration: timecode.duration ?? null,
      position: timecode.position ?? null,
      linkedContentId:
        timecode.linkedContentId ?? timecode.linkedContent?.id ?? null,
    }));

    void updateTimecodes({
      variables: { id: contentId, input: { timecodes: cleanTimecodes } },
    });
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      if (!window.confirm(t("videos.timecodes.unsavedChangesWarning"))) {
        return;
      }
    }
    onClose();
  };

  // Метки для превью-плеера: локальные правки видны сразу
  const playerTimecodes = useMemo(
    () =>
      localTimecodes.map((timecode, index) => ({
        id: timecode.id ?? `draft-${index}`,
        type: timecode.type,
        time: timecode.time,
        title: timecode.title,
        description: timecode.description,
        icon: timecode.icon,
        duration: timecode.duration,
        position: timecode.position,
      })),
    [localTimecodes]
  );

  return (
    <>
      <AppBar color="default" position="fixed" elevation={0}>
        <Toolbar>
          <IconButton edge="start" onClick={handleClose} sx={{ mr: 1 }}>
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" noWrap sx={{ flex: 1, mr: 1 }}>
            {t("videos.timecodes.header")}
          </Typography>
          {hasUnsavedChanges && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? t("common.actions.saving") : t("common.actions.save")}
            </Button>
          )}
        </Toolbar>
        <Divider />
      </AppBar>

      <Box sx={{ mt: { xs: "56px", sm: "64px" } }}>{menu}</Box>

      <Box sx={{ p: 1 }}>
        <Grid
          container
          spacing={1}
          flexDirection={isMobile ? "column-reverse" : "row"}
        >
          <Grid size={{ xs: 12, sm: 6, md: 7 }}>
            <Card elevation={0}>
              <TimecodesList
                timecodes={localTimecodes}
                loading={loading}
                onClickEdit={onClickEdit}
                onTimecodeClick={onTimecodeClick}
                onDeleteTimecode={onDeleteTimecode}
                onCreateTimecode={onClickCreateTimecode}
                videoDuration={videoDuration}
              />
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 5 }}>
            <Card elevation={0}>
              {hlsUrl ? (
                <Player
                  src={hlsUrl}
                  poster={thumbnailUrl ?? null}
                  autoPlay={false}
                  hotkeys={false}
                  timecodes={playerTimecodes}
                  getVideoElement={(video) => {
                    videoElRef.current = video;
                  }}
                />
              ) : (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t(providerTextKeys.playerPreviewProcessing)}
                  </Typography>
                </Box>
              )}
            </Card>
          </Grid>
        </Grid>
      </Box>

      <TimecodeFormDialog
        open={dialogMode !== null}
        onClose={onCloseFormDialog}
        mode={dialogMode}
        currentTime={dialogCurrentTime}
        timecode={editTimecode}
        videoDuration={videoDuration}
        onCompleted={onFormCompleted}
      />
    </>
  );
};
